import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaPlus, 
  FaTrash,
  FaSearch,
  FaFileInvoiceDollar,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import { expenseService, type CreateExpenseEntry, type ExpenseHead, type Expense } from '../services/expenseService';
import { projectService } from '../services/projectService';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenMode } from '../contexts/HiddenModeContext';
import '../css/pages/AddExpenses.css';
import '../css/hiddenIncome.css';  // For unauthorized access styles

interface Project {
  id: number;
  name: string;
}

interface ExpenseRow extends CreateExpenseEntry {
  tempId: string;
  errors: string[];
}

const AddExpenses: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Expense Head Creation
  const [showNewExpenseHeadForm, setShowNewExpenseHeadForm] = useState(false);
  const [newExpenseHead, setNewExpenseHead] = useState({
    name: '',
    description: ''
  });
  
  // Expense Rows
  const [expenseRows, setExpenseRows] = useState<ExpenseRow[]>([
    {
      tempId: `row_${Date.now()}`,
      expenseHeadId: '',  // Changed from 0 to empty string to match CUID format
      expenseDate: new Date().toISOString().split('T')[0],
      voucherNo: '',
      narration: '',
      amount: 0,
      errors: []
    }
  ]);

  useEffect(() => {
    const path = window.location.pathname;
    const hasId = searchParams.get('id');
    
    // If trying to edit or view without an ID, redirect to expenses list
    if ((path.includes('/edit') || path.includes('/view')) && !hasId) {
      navigate('/expenses');
      return;
    }
    
    if (path.includes('/view') || searchParams.get('mode') === 'view') {
      setIsView(true);
    } else if (path.includes('/edit') && hasId) {
      setIsEdit(true);
    }
    
    loadInitialData();
  }, [searchParams, navigate]);

  // Separate useEffect for loading expense data when in edit/view mode
  useEffect(() => {
    if (id && (isEdit || isView)) {
      loadExpense();
    }
  }, [id, isEdit, isView]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadProjects(),
        loadExpenseHeads()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response.filter((p: any) => p.isActive));
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadExpenseHeads = async () => {
    try {
      const response = await expenseService.getExpenseHeads();
      setExpenseHeads(response);
    } catch (error) {
      console.error('Error loading expense heads:', error);
    }
  };

  const loadExpense = async () => {
    try {
      setLoading(true);
      
      // Load both projects and expense data
      const [projectsData, expense] = await Promise.all([
        projectService.getProjects(),
        expenseService.getExpenseById(id!)
      ]);
      
      // Set the project
      const project = projectsData.find(p => String(p.id) === String(expense.projectId));
      if (project) {
        setSelectedProject(project);
      }
      
      // Set the single expense entry
      const expenseRow = {
        tempId: 'edit_1',
        expenseHeadId: String(expense.expenseHeadId), // Convert to string
        expenseDate: expense.expenseDate.split('T')[0], // Convert to YYYY-MM-DD format
        voucherNo: expense.voucherNo || '',
        narration: expense.narration || '',
        amount: expense.amount,
        errors: []
      };
      
      setExpenseRows([expenseRow]);
    } catch (error) {
      console.error('Error loading expense:', error);
      alert('Error loading expense');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
  };

  const handleExpenseRowChange = (tempId: string, field: keyof CreateExpenseEntry, value: any) => {
    setExpenseRows(rows =>
      rows.map(row =>
        row.tempId === tempId
          ? { ...row, [field]: value, errors: [] }
          : row
      )
    );
  };

  const addExpenseRow = () => {
    const newRow: ExpenseRow = {
      tempId: `row_${Date.now()}`,
      expenseHeadId: '',  // Changed from 0 to empty string to match CUID format
      expenseDate: new Date().toISOString().split('T')[0],
      voucherNo: '',
      narration: '',
      amount: 0,
      errors: []
    };
    setExpenseRows(rows => [...rows, newRow]);
  };

  const removeExpenseRow = (tempId: string) => {
    if (expenseRows.length > 1) {
      setExpenseRows(rows => rows.filter(row => row.tempId !== tempId));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    if (!selectedProject) {
      alert('Please select a project');
      return false;
    }

    const updatedRows = expenseRows.map(row => {
      const errors: string[] = [];
      
      if (!row.expenseHeadId || row.expenseHeadId.trim() === '') {
        errors.push('Expense head is required');
      }
      
      if (!row.expenseDate) {
        errors.push('Expense date is required');
      }
      
      if (!row.amount || row.amount <= 0) {
        errors.push('Amount must be greater than 0');
      }
      
      if (errors.length > 0) {
        isValid = false;
      }
      
      return { ...row, errors };
    });

    setExpenseRows(updatedRows);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      if (isEdit && id) {
        // Update single expense
        const expenseData = expenseRows[0];
        await expenseService.updateExpense(id, {
          expenseHeadId: expenseData.expenseHeadId,
          expenseDate: expenseData.expenseDate,
          voucherNo: expenseData.voucherNo || undefined,
          narration: expenseData.narration || undefined,
          amount: expenseData.amount
        });
      } else {
        // Create new expenses
        const expenseData: CreateExpenseEntry[] = expenseRows.map(row => ({
          expenseHeadId: row.expenseHeadId,
          expenseDate: row.expenseDate,
          voucherNo: row.voucherNo || undefined,
          narration: row.narration || undefined,
          amount: row.amount
        }));

        await expenseService.createExpenses({ 
          projectId: selectedProject!.id,
          expenses: expenseData 
        });
      }
      
      navigate('/ledger/expenses');
    } catch (error) {
      console.error('Error saving expenses:', error);
      alert('Failed to save expenses. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateExpenseHead = async () => {
    if (!newExpenseHead.name.trim()) {
      alert('Expense head name is required');
      return;
    }

    try {
      const createdHead = await expenseService.createExpenseHead({
        name: newExpenseHead.name.trim(),
        description: newExpenseHead.description.trim() || undefined
      });
      
      setExpenseHeads(prev => [...prev, createdHead]);
      setNewExpenseHead({ name: '', description: '' });
      setShowNewExpenseHeadForm(false);
    } catch (error) {
      console.error('Error creating expense head:', error);
      alert('Failed to create expense head. Please try again.');
    }
  };

  const calculateTotal = (): number => {
    return expenseRows.reduce((total, row) => total + (row.amount || 0), 0);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Redirect if not in hidden mode
  if (!isHiddenModeActive) {
    return (
      <div className="page-container">
        <div className="unauthorized-access">
          <div className="unauthorized-content">
            <h2>🔒 Access Restricted</h2>
            <p>Expense Management is only available when Hidden Mode is active.</p>
            <p>Please activate Hidden Mode to access these features.</p>
            <div style={{marginTop: '20px', fontSize: '14px', color: '#666'}}>
              <p><strong>How to activate:</strong></p>
              <p>Press <code>Alt + Ctrl + Shift + 6</code> to activate Hidden Mode</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}>
            <FaFileInvoiceDollar className="page-icon" style={{ marginRight: '8px' }} /> 
            {isView ? 'View Expense' : isEdit ? 'Edit Expense' : 'Add Expenses'}
          </h1>
        </div>
        <button 
          className="back-btn"
          onClick={() => navigate('/ledger/expenses')}
        >
          <FaArrowLeft />
        </button>
      </div>

      {/* Project Selection - only show in add mode */}
      {!isEdit && !isView && (
        <div className="form-container">
          <h2>Select Project</h2>
          
          {!selectedProject ? (
          <div className="project-selection">
            <div className="project-search-bar">
              <FaSearch />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
              />
            </div>
            
            <div className="project-list">
              {filteredProjects.map(project => (
                <div
                  key={project.id}
                  className="project-item"
                  onClick={() => handleProjectSelect(project)}
                >
                  <div className="project-info">
                    <h3>{project.name}</h3>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredProjects.length === 0 && (
              <div className="no-projects">
                No projects found matching "{projectSearch}"
              </div>
            )}
          </div>
        ) : (
          <div className="selected-project-info">
            <h3>{selectedProject.name}</h3>
            <button 
              className="change-project-btn"
              onClick={() => setSelectedProject(null)}
            >
              Change Project
            </button>
          </div>
        )}
      </div>
      )}

      {/* Show selected project info in edit/view mode */}
      {(isEdit || isView) && selectedProject && (
        <div className="form-container">
          <div className="selected-project-info">
            <h3>Project: {selectedProject.name}</h3>
          </div>
        </div>
      )}

      {/* Expense Head Management */}
      {selectedProject && (
        <div className="form-container">
          <div className="expense-head-header">
            <h2>Expense Heads</h2>
            {!isEdit && !isView && (
              <button
                className="add-expense-head-btn"
                onClick={() => setShowNewExpenseHeadForm(!showNewExpenseHeadForm)}
              >
                <FaPlus /> Add New Expense Head
              </button>
            )}
          </div>
          
          {!isEdit && !isView && showNewExpenseHeadForm && (
            <div className="new-expense-head-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Expense Head Name *</label>
                  <input
                    type="text"
                    value={newExpenseHead.name}
                    onChange={(e) => setNewExpenseHead(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter expense head name"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={newExpenseHead.description}
                    onChange={(e) => setNewExpenseHead(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter description (optional)"
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-save" onClick={handleCreateExpenseHead}>
                    <FaSave /> Save
                  </button>
                  <button className="btn-cancel" onClick={() => setShowNewExpenseHeadForm(false)}>
                    <FaTimes /> Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expense Entries */}
      {selectedProject && (
        <div className="form-container">
          <div className="expense-entries-header">
            <h2>Expense Entries</h2>
            {!isEdit && !isView && (
              <button className="add-row-btn" onClick={addExpenseRow}>
                <FaPlus /> Add Row
              </button>
            )}
          </div>
          
          <div className="content-section">
            <table className="standard-table">
              <thead>
                <tr>
                  <th>Expense Head *</th>
                  <th>Date *</th>
                  <th>Voucher No</th>
                  <th>Narration</th>
                  <th>Amount *</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenseRows.map((row, index) => (
                  <tr key={row.tempId} className={row.errors.length > 0 ? 'error-row' : ''}>
                    <td>
                      <select
                        value={row.expenseHeadId}
                        onChange={(e) => handleExpenseRowChange(row.tempId, 'expenseHeadId', e.target.value)}
                        className={row.errors.some(e => e.includes('Expense head')) ? 'error' : ''}
                      >
                        <option value="">Select Expense Head</option>
                        {expenseHeads.map(head => (
                          <option key={head.id} value={head.id}>
                            {head.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="date"
                        value={row.expenseDate}
                        onChange={(e) => handleExpenseRowChange(row.tempId, 'expenseDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className={row.errors.some(e => e.includes('date')) ? 'error' : ''}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={row.voucherNo}
                        onChange={(e) => handleExpenseRowChange(row.tempId, 'voucherNo', e.target.value)}
                        placeholder="Voucher number"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={row.narration}
                        onChange={(e) => handleExpenseRowChange(row.tempId, 'narration', e.target.value)}
                        placeholder="Description/notes"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={row.amount === 0 ? '' : row.amount || ''}
                        onChange={(e) => handleExpenseRowChange(row.tempId, 'amount', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        className={row.errors.some(e => e.includes('Amount')) ? 'error' : ''}
                      />
                    </td>
                    <td>
                      {!isEdit && !isView && (
                        <button
                          className="btn-icon delete"
                          onClick={() => removeExpenseRow(row.tempId)}
                          disabled={expenseRows.length === 1}
                          title="Remove Row"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={4}><strong>Entries Count:</strong></td>
                  <td><strong>{expenseRows.length} entries</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          {/* Show errors for each row */}
          {expenseRows.some(row => row.errors.length > 0) && (
            <div className="form-errors">
              <h4>Please fix the following errors:</h4>
              {expenseRows.map((row, index) => 
                row.errors.length > 0 && (
                  <div key={row.tempId} className="row-errors">
                    <strong>Row {index + 1}:</strong>
                    <ul>
                      {row.errors.map((error, errorIndex) => (
                        <li key={errorIndex}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary and Actions */}
      {selectedProject && (
        <div className="form-container">
          <div className="summary-section">
            <h3>Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Project:</span>
                <span className="summary-value">{selectedProject.name}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Entries:</span>
                <span className="summary-value">{expenseRows.length}</span>
              </div>
              {isHiddenModeActive && (
                <div className="summary-item">
                  <span className="summary-label">Database:</span>
                  <span className="summary-value amount" style={{color: '#dc3545'}}>Receivables (Encrypted)</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              className="btn-secondary"
              onClick={() => navigate('/ledger/expenses')}
            >
              Cancel
            </button>
            <button 
              className="btn-primary"
              onClick={handleSubmit}
              disabled={submitting || expenseRows.length === 0}
            >
              {submitting 
                ? (isEdit ? 'Updating...' : 'Creating...') 
                : (isEdit ? 'Update Expense' : 'Create Expense Entries')
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddExpenses;
